import { useState, useEffect, useCallback } from "react";
import { loadFromSupabase } from "../supabaseDB";
import { computeAccuracy, ACCURACY_MIN_MATCHES, ACCURACY_HORIZON_DAYS, ACCURACY_MAX_MATCHES } from "./accuracyCalc";

/* GFI Nexus — Accuracy Dashboard, Fase D of the Forecast v2 roadmap
   ─────────────────────────────────────────────────────────────────
   Real implementation (2026-09-15), replacing the ComingSoon
   placeholder. No new table: ai_analyst_log already accumulates one
   real daily snapshot per category (base_forecast, adjusted_price,
   context.lastRealPrice) — see accuracyCalc.js's header for how that's
   enough to build a genuine projection-vs-reality comparison without
   fabricating anything. Since ai_analyst_log only started accumulating
   real rows today (2026-09-15), this screen starts with an honest
   "not enough data yet" state per category and fills in automatically
   as the daily cron keeps running — never shows a MAPE% computed from
   too few real matched pairs (ACCURACY_MIN_MATCHES). */

var T = {
  en: {
    title: "Accuracy Dashboard",
    subtitle: "How well the base trend forecast and Claude's adjusted price actually predicted reality, last " + ACCURACY_MAX_MATCHES + " matched weeks.",
    refresh: "Refresh",
    empty: "No AI Analyst Log history yet — this fills in automatically once the daily analysis has been running a while.",
    notEnough: "Not enough matched history yet (need at least " + ACCURACY_MIN_MATCHES + " real projection-vs-reality pairs, " + ACCURACY_HORIZON_DAYS + " days apart each). This category will show real numbers once the daily analysis has run for a couple of weeks.",
    mapeBase: "MAPE — base trend", mapeAdjusted: "MAPE — Claude-adjusted",
    matchesUsed: "matched weeks used",
    alertHigh: "⚠ Error above 15% — consider reviewing the model/prompt for this category.",
    tableProjected: "Projected", tableRealized: "Realized", tableDate: "Date", tableBase: "Base", tableAdjusted: "Adjusted", tableBaseErr: "Base err.", tableAdjErr: "Adj. err.",
    claudeHelping: "Claude's adjustment is improving accuracy vs the base trend.",
    claudeHurting: "Claude's adjustment is currently making things less accurate than the base trend alone.",
  },
  es: {
    title: "Dashboard de Precisión",
    subtitle: "Qué tan bien predijeron la realidad el pronóstico base y el precio ajustado por Claude, últimas semanas comparables.",
    refresh: "Actualizar",
    empty: "Todavía no hay historial de AI Analyst Log — esto se llena automáticamente conforme el análisis diario lleve un tiempo corriendo.",
    notEnough: "Todavía no hay suficiente historial comparado (se necesitan al menos " + ACCURACY_MIN_MATCHES + " pares reales de proyección vs realidad, con " + ACCURACY_HORIZON_DAYS + " días de separación). Esta categoría mostrará números reales cuando el análisis diario lleve un par de semanas corriendo.",
    mapeBase: "MAPE — tendencia base", mapeAdjusted: "MAPE — ajustado por Claude",
    matchesUsed: "semanas comparadas usadas",
    alertHigh: "⚠ Error por encima de 15% — considera revisar el modelo/prompt de esta categoría.",
    tableProjected: "Proyectado", tableRealized: "Real", tableDate: "Fecha", tableBase: "Base", tableAdjusted: "Ajustado", tableBaseErr: "Err. base", tableAdjErr: "Err. ajuste",
    claudeHelping: "El ajuste de Claude está mejorando la precisión respecto a la tendencia base.",
    claudeHurting: "El ajuste de Claude actualmente empeora la precisión respecto a la tendencia base sola.",
  },
};

var CATEGORY_LABEL = { en: { Conventional: "Conventional", Organic: "Organic" }, es: { Conventional: "Convencional", Organic: "Orgánico" } };

function fmtDate(dateStr, lang) {
  try { return new Date(dateStr + "T00:00:00").toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { month: "short", day: "2-digit" }); }
  catch (e) { return dateStr; }
}

function CategoryCard({ category, data, lang, t }) {
  var catLabel = CATEGORY_LABEL[lang === "es" ? "es" : "en"][category] || category;
  var highError = data.sufficient && data.mapeAdjusted != null && data.mapeAdjusted > 15;
  var claudeBetter = data.sufficient && data.mapeBase != null && data.mapeAdjusted != null && data.mapeAdjusted < data.mapeBase;
  var claudeWorse = data.sufficient && data.mapeBase != null && data.mapeAdjusted != null && data.mapeAdjusted > data.mapeBase;

  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{catLabel}</div>

      {!data.sufficient ? (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(107,114,128,0.08)", border: "1px solid var(--color-border)", fontSize: 11.5, color: "var(--color-text-muted)" }}>
          {t.notEnough}
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.mapeBase}</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{data.mapeBase}%</div>
            </div>
            <div>
              <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.mapeAdjusted}</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: highError ? "#DC2626" : "var(--color-accent)" }}>{data.mapeAdjusted}%</div>
            </div>
            <div>
              <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.matchesUsed}</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{data.matchCount}</div>
            </div>
          </div>

          {highError && (
            <div style={{ padding: "8px 12px", borderRadius: 6, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 11, marginBottom: 10 }}>{t.alertHigh}</div>
          )}
          {(claudeBetter || claudeWorse) && (
            <div style={{ padding: "8px 12px", borderRadius: 6, background: claudeBetter ? "rgba(5,150,105,0.08)" : "rgba(217,119,6,0.08)", border: "1px solid " + (claudeBetter ? "rgba(5,150,105,0.25)" : "rgba(217,119,6,0.25)"), color: claudeBetter ? "#059669" : "#B45309", fontSize: 11, marginBottom: 12 }}>
              {claudeBetter ? t.claudeHelping : t.claudeHurting}
            </div>
          )}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--color-text-muted)", borderBottom: "1px solid var(--color-border)" }}>
                  <th style={{ padding: "4px 8px" }}>{t.tableDate}</th>
                  <th style={{ padding: "4px 8px" }}>{t.tableBase}</th>
                  <th style={{ padding: "4px 8px" }}>{t.tableAdjusted}</th>
                  <th style={{ padding: "4px 8px" }}>{t.tableRealized}</th>
                  <th style={{ padding: "4px 8px" }}>{t.tableBaseErr}</th>
                  <th style={{ padding: "4px 8px" }}>{t.tableAdjErr}</th>
                </tr>
              </thead>
              <tbody>
                {data.matches.map(function (m, i) {
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace" }}>{fmtDate(m.projectedDate, lang)}</td>
                      <td style={{ padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace" }}>${m.baseForecast}</td>
                      <td style={{ padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace" }}>{m.adjustedPrice != null ? "$" + m.adjustedPrice : "—"}</td>
                      <td style={{ padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>${m.realized}</td>
                      <td style={{ padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace" }}>{m.baseErrorPct}%</td>
                      <td style={{ padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace" }}>{m.adjustedErrorPct != null ? m.adjustedErrorPct + "%" : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function AccuracyDashboard({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "es" ? "es" : "en"];
  var [entries, setEntries] = useState([]);
  var [loading, setLoading] = useState(true);

  var load = useCallback(function () {
    setLoading(true);
    loadFromSupabase("ai_analyst_log", { force: true }).then(function (rows) {
      setEntries(rows || []);
    }).catch(function () { setEntries([]); }).finally(function () { setLoading(false); });
  }, []);

  useEffect(function () { load(); }, [load]);

  var accuracy = computeAccuracy(entries);
  var categories = Object.keys(accuracy).sort();

  return (
    <div style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: 0 }}>{t.subtitle}</p>
        </div>
        <button
          type="button" onClick={load} disabled={loading}
          style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "rgba(42,122,111,0.08)", color: "var(--color-accent)", cursor: loading ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
        >
          {t.refresh}
        </button>
      </div>

      {!loading && categories.length === 0 ? (
        <div style={{ padding: "24px 16px", textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", fontSize: 12.5 }}>{t.empty}</div>
      ) : (
        categories.map(function (cat) {
          return <CategoryCard key={cat} category={cat} data={accuracy[cat]} lang={lang} t={t} />;
        })
      )}
    </div>
  );
}
